import * as d3 from 'd3';
import { shortenNumber } from '../licenseAnalyticsSupport/calculationsUtils';
import './BoxPlot.css';
import { OPTIONSENUM } from '../licenseAnalyticsSupport/filtersOptions';

const D3BoxPlot = (node:any, data:any, t:any, benchmarkingValue:any) => {
  // set the dimensions and margins of the graph
  const margin = { top: 10, right: 30, bottom: 50, left: 140 },
    width = 390 - margin.left - margin.right,
    height = data.length > 5 ? data.length * 50 : 300 - margin.top - margin.bottom; // Calculate total height based on number of data items

  // append the svg object to the body of the page
  const svg = d3
    .select(node)
    .append('svg')
    .attr('width', width + margin.left + margin.right)
    .attr('height', height + margin.top + margin.bottom)
    .append('g')
    .attr('transform', 'translate(' + margin.left + ',' + margin.top + ')');

  // Function to create and show the tooltip
  function createTooltip(): HTMLDivElement {
    const oldTooltip:any = document.getElementsByClassName('boxplottooltip');
    for (const oldElement of oldTooltip) {
      oldElement.remove();
    }
    const tooltipLocal = document.createElement('div');

    tooltipLocal.className = 'boxplottooltip';

    tooltipLocal.style.position = 'absolute';
    tooltipLocal.style.opacity = '0';
    document.body.appendChild(tooltipLocal);
    return tooltipLocal;
  }

  // Three function that change the tooltip when user hover / move / leave a cell
  const tooltip = createTooltip();

  const mouseover = function(d:any, obj:any): void {
    tooltip.innerHTML = `
    <div class="tooltip-header">${obj.x}</div>
    ${
      obj.z
        ? `<div class="tooltip-entry">
            <div class="marker-wrapper">
              <div class="tooltip-marker" style="background-color: rgb(46, 147, 250);"></div>
              <span class="tooltip-label">${t('CLUB_VALUE')}:</span>
            </div>
            <span class="tooltip-value">${
              benchmarkingValue?.id == OPTIONSENUM.ACADEMYCOSTSCLUBCOSTS
                ? obj.z?.toFixed?.(1) + '%'
                : shortenNumber(obj.z)
            }</span>
          </div>`
        : ``
    }
    <div class="tooltip-entry">
      <div class="marker-wrapper">
        <div class="tooltip-marker" style="background-color: rgb(237, 138, 25);"></div>
        <span class="tooltip-label">${t('AVERAGE')}: </span>
      </div>
      <span class="tooltip-value">${
        benchmarkingValue?.id == OPTIONSENUM.ACADEMYCOSTSCLUBCOSTS
          ? obj.y[2]?.toFixed?.(1) + '%'
          : shortenNumber(obj.y[2])
      }</span>
    </div>
    <div class="tooltip-entry">
      <span class="tooltip-label">${t('MIN_CLUB_VALUE')}: </span>
      <span class="tooltip-value">${
        benchmarkingValue?.id == OPTIONSENUM.ACADEMYCOSTSCLUBCOSTS
          ? obj.y[0]?.toFixed?.(1) + '%'
          : shortenNumber(obj.y[0])
      }</span>
    </div>
    <div class="tooltip-entry">
      <span class="tooltip-label">25% ${t('OF_THE_RANGE')} : </span>
      <span class="tooltip-value">${
        benchmarkingValue?.id == OPTIONSENUM.ACADEMYCOSTSCLUBCOSTS
          ? obj.y[1]?.toFixed?.(1) + '%'
          : shortenNumber(obj.y[1])
      }</span>
    </div>
    <div class="tooltip-entry">
      <span class="tooltip-label">75% ${t('OF_THE_RANGE')} : </span>
      <span class="tooltip-value">${
        benchmarkingValue?.id == OPTIONSENUM.ACADEMYCOSTSCLUBCOSTS
          ? obj.y[3]?.toFixed?.(1) + '%'
          : shortenNumber(obj.y[3])
      }</span>
    </div>
    <div class="tooltip-entry">
      <span class="tooltip-label">${t('MAX_CLUB_VALUE')}: </span>
      <span class="tooltip-value">${
        benchmarkingValue?.id == OPTIONSENUM.ACADEMYCOSTSCLUBCOSTS
          ? obj.y[4]?.toFixed?.(1) + '%'
          : shortenNumber(obj.y[4])
      }</span>
    </div>
  `;
    tooltip.style.opacity = '1';
  };
  const mousemove = function(d:any) {
    tooltip.style.left = d.pageX + 20 + 'px';
    tooltip.style.top = d.pageY + 'px';
  };
  const mouseleave = function(d:any) {
    tooltip.style.opacity = '0';
  };

  // Show the Y scale
  const y:any = d3
    .scaleBand()
    .range([height, 0])
    .domain(data.map((obj:any) => obj.x))
    .padding(0.4);
  svg
    .append('g')
    .attr('stroke-width', 0.5)
    .attr('font-weight', 500)
    .call(
      d3.axisLeft(y).tickFormat((text:any) => {
        if (text.length > 20) {
          return text.substring(0, 19) + '...';
        }
        return text;
      }),
    );
  const minimumRange:any = d3.min(data, (obj:any) => obj.y[0]);
  const maximumRange:any = d3.max(data, (obj:any) => obj.y[4]);

  const range = [minimumRange * 1.2, maximumRange * 1.2];
  // Show the X scale
  const x = d3
    .scaleLinear()
    .domain(range)
    .range([0, width]);
  svg
    .append('g')
    .attr('transform', 'translate(0,' + height + ')')
    .attr('stroke-width', 0.5)
    .attr('font-weight', 500)
    .call(
      d3
        .axisBottom(x)
        .ticks(4)
        .tickFormat(shortenNumber),
    );

  // Show the main vertical line
  svg
    .selectAll('vertLines')
    .data(data)
    .enter()
    .append('line')
    .attr('x1', function(d:any) {
      return x(d.y[0]);
    })
    .attr('x2', function(d:any) {
      return x(d.y[4]);
    })
    .attr('y1', function(d:any) {
      return y(d.x) + y.bandwidth() / 2;
    })
    .attr('y2', function(d:any) {
      return y(d.x) + y.bandwidth() / 2;
    })
    .attr('stroke', 'black')
    .style('width', 40)
    .on('mouseover', function(e:any, d:any) {
      mouseover(e, d);
    })
    .on('mouseout', function(e:any) {
      mouseleave(e);
    })
    .on('mousemove', function(e:any) {
      mousemove(e);
    });

  // rectangle for the main box
  svg
    .selectAll('boxes')
    .data(data)
    .enter()
    .append('rect')
    .attr('x', function(d:any) {
      return x(d.y[1]);
    })
    .attr('width', function(d:any) {
      return x(d.y[3]) - x(d.y[1]);
    })
    .attr('y', function(d:any) {
      return y(d.x);
    })
    .attr('height', y.bandwidth())
    .attr('stroke', 'black')
    .style('fill', '#eceff1')
    .on('mouseover', function(e:any, d:any) {
      mouseover(e, d);
    })
    .on('mouseout', function(e:any) {
      mouseleave(e);
    })
    .on('mousemove', function(e:any) {
      mousemove(e);
    });

  // Show the median line
  svg
    .selectAll('medianLines')
    .data(data)
    .enter()
    .append('line')
    .attr('y1', function(d:any) {
      return y(d.x);
    })
    .attr('y2', function(d:any) {
      return y(d.x) + y.bandwidth();
    })
    .attr('x1', function(d:any) {
      return x(d.y[2]);
    })
    .attr('x2', function(d:any) {
      return x(d.y[2]);
    })
    .attr('stroke', 'black')
    .style('stroke', '#ED8A19')
    .style('stroke-width', 3)
    .style('width', 80)
    .on('mouseover', function(e:any, d:any) {
      mouseover(e, d);
    })
    .on('mouseout', function(e:any) {
      mouseleave(e);
    })
    .on('mousemove', function(e:any) {
      mousemove(e);
    });

  // Show the max line
  svg
    .selectAll('maxLines')
    .data(data)
    .enter()
    .append('line')
    .attr('y1', function(d:any) {
      return y(d.x);
    })
    .attr('y2', function(d:any) {
      return y(d.x) + y.bandwidth();
    })
    .attr('x1', function(d:any) {
      return x(d.y[4]);
    })
    .attr('x2', function(d:any) {
      return x(d.y[4]);
    })
    .attr('stroke', 'black')
    .style('width', 80)
    .on('mouseover', function(e:any, d:any) {
      mouseover(e, d);
    })
    .on('mouseout', function(e:any) {
      mouseleave(e);
    })
    .on('mousemove', function(e:any) {
      mousemove(e);
    });

  // Show the min line
  svg
    .selectAll('minLines')
    .data(data)
    .enter()
    .append('line')
    .attr('y1', function(d:any) {
      return y(d.x);
    })
    .attr('y2', function(d:any) {
      return y(d.x) + y.bandwidth();
    })
    .attr('x1', function(d:any) {
      return x(d.y[0]);
    })
    .attr('x2', function(d:any) {
      return x(d.y[0]);
    })
    .attr('stroke', 'black')
    .style('width', 80)
    .on('mouseover', function(e:any, d:any) {
      mouseover(e, d);
    })
    .on('mouseout', function(e:any) {
      mouseleave(e);
    })
    .on('mousemove', function(e:any) {
      mousemove(e);
    });

  if (data?.length > 0 && data[0].z != undefined && data[0].z != null) {
    // Add club data as point
    svg
      .selectAll('indPoints')
      .data(data)
      .enter()
      .append('circle')
      .attr('cx', function(d:any) {
        return x(d.z);
      })
      .attr('cy', function(d:any) {
        return y(d.x) + y.bandwidth() / 2 - 1 / 2 + Math.random();
      })
      .attr('r', 4)
      .style('fill', function(d:any) {
        return '#2E93fA';
      })
      .attr('stroke', 'black');
  }
  d3.selectAll('.tick')
    .append('title')
    .text((d:any) => d);
};
export default D3BoxPlot;
